import React, {ReactElement} from 'react';
import {Controller} from 'react-hook-form';
import {Box, TextField} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import {useTranslation} from 'react-i18next';

import {RHFCheckbox} from 'src/minimal-components/hook-form';
import {FormControlDiv} from '../../../module-search-filter/ModuleSearchFilterStyle';
import BasicDatePicker from '@components/form/date-picker/BasicDatePicker';
import SelectInput from '@components/form/select-input/SelectInput';
import {VisuallyHiddenInput} from './FormContainerStyles';

import FormConstants from '@shared/constants/FormConstants';

const FormContainer = (props: any) => {
  const {t} = useTranslation();
  const {formMethods, uiDisabled, uiFields} = props;
  const uiFieldsArray = Object.keys(uiFields);

  const renderTextField = (
    inputName: string,
    item: any,
    formType: string
  ): ReactElement => {
    return (
      <Controller
        name={inputName}
        control={formMethods.control}
        render={({field: {value, onChange}}) => (
          <FormControlDiv>
            <TextField
              disabled={uiDisabled}
              value={value || ''}
              type={item.type}
              label={
                item?.heading?.[0]
                  ? item?.heading?.[0] || item?.heading?.[1]
                  : ''
              }
              fullWidth
              placeholder={item?.placeholder?.[0] ? item?.placeholder?.[0] : ''}
              onChange={(e: any) => {
                props?.handleTextFieldChange(e, inputName, formType);
                onChange(e);
              }}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0000003b',
                  borderWidth: '1.8px'
                }
              }}
            />
          </FormControlDiv>
        )}
      />
    );
  };

  const renderDateField = (
    inputName: string,
    item: any,
    formType: any
  ): ReactElement => {
    return (
      <Controller
        name={inputName}
        control={formMethods.control}
        defaultValue={null}
        render={({field}) => (
          <BasicDatePicker
            disabled={uiDisabled}
            yearFormatView={item?.yearFormatView ? item?.yearFormatView : false}
            label={
              item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
            }
            error={!!formMethods.formState.errors[inputName]}
            helperText={
              `${item.heading?.[0]}` +
              t('PRIVATE.COMMON.IS_REQUIRED', {ns: 'private'})
            }
            field={field}
            handleChange={() => {
              // console.log(getPaymentDateOfService());
            }}
          />
        )}
      />
    );
  };

  const renderSelectField = (
    inputName: string,
    item: any,
    formType: string
  ): ReactElement => {
    const optionsList = props?.getSelectFieldOptions(inputName, formType);
    return (
      <Controller
        name={inputName}
        control={formMethods.control}
        defaultValue={''}
        render={({field: {value, onChange}}) => (
          <FormControlDiv>
            <SelectInput
              disabled={uiDisabled}
              enableDefaultOption
              options={optionsList}
              label={
                item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''
              }
              placeholder={item.placeholder}
              name={inputName}
              value={value || ''}
              fullWidth
              onChange={(event: any) => {
                onChange(event);
                props?.handleSelectFieldChange(event, inputName);
              }}
              optionValueKey={'value'}
              optionLabelKey={'label'}
            />
          </FormControlDiv>
        )}
      />
    );
  };

  const renderCheckBoxButtonField = (
    inputName: string,
    item: any,
    formType: any
  ): ReactElement => {
    return (
      <RHFCheckbox
        disabled={uiDisabled}
        name={inputName}
        label={item.heading[0] ? item.heading?.[0] || item.heading?.[1] : ''}
        sx={{my: '5px'}}
      />
    );
  };

  const renderUploadFileField = (
    inputName: string,
    item: any,
    formType: any
  ): ReactElement => {
    return (
      <LoadingButton
        disabled={uiDisabled}
        fullWidth
        key={inputName}
        component="label"
        variant="contained"
        loading={false}
        sx={{
          height: '50px'
        }}>
        {item?.heading?.[0] ? item?.heading?.[0] || item?.heading?.[1] : ''}
        <VisuallyHiddenInput type="file" onChange={props?.handleFileChange} />
      </LoadingButton>
    );
  };

  const renderButtonField = (inputName: string, item: any, formType: any) => {
    return (
      <LoadingButton
        type="button"
        fullWidth
        key={inputName}
        variant={item?.variant || 'contained'}
        loading={props?.getBtnLoadingStatus(inputName, formType)}
        disabled={props?.getBtnDisabledStatus(inputName, formType)}
        onClick={(e: any) =>
          props?.handleButtonFieldClick(e, inputName, formType)
        }
        sx={{...props?.getButtonColorSx(inputName, formType)}}>
        {item?.heading?.[0] ? item?.heading?.[0] || item?.heading?.[1] : ''}
      </LoadingButton>
    );
  };

  const renderFormFields = (field: string, item: any, formType: string) => {
    switch (item.type) {
      case FormConstants.FIELD_TYPES.DATE:
        return renderDateField(field, item, formType);
      case FormConstants.FIELD_TYPES.SELECT:
        return renderSelectField(field, item, formType);
      case FormConstants.FIELD_TYPES.CHECKBOX:
        return renderCheckBoxButtonField(field, item, formType);
      case 'file':
        return renderUploadFileField(field, item, formType);
      case 'button':
        return renderButtonField(field, item, formType);
      default:
        return renderTextField(field, item, formType);
    }
  };

  return (
    <Box
      rowGap={3}
      columnGap={2}
      display="grid"
      p={1}
      gridTemplateColumns={{
        xs: 'repeat(1, 1fr)',
        sm: `repeat(${props?.gridItemsPerRow || 4}, 1fr)`
      }}>
      {uiFieldsArray?.map((item: any, index: number) => {
        const key = `${item?.key}-${index}`;
        return (
          <Box key={key}>
            {renderFormFields(item, uiFields[item], props?.formType)}
          </Box>
        );
      })}
    </Box>
  );
};

export default FormContainer;
